import React, { useState, useEffect, useRef } from 'react';
import SelectFieldOption from './components/SelectFieldOption';
import { SelectFieldContext } from './context/SelectFieldContext';
// @ts-ignore
import classNames from 'classnames';
import styles from './SelectField.module.scss';
import SelectFieldSelected from './components/SelectFieldSelected';
import SelectFieldList from './components/SelectFieldList';

enum DirectionEnum {
  Top = 'top',
}

export type SelectOptionType = {
  name: string;
  value: string;
};

type SelectFieldPropsType = {
  id?: string;
  name: string;
  className?: string;
  selectedFieldClassname?: string;
  fieldListClassname?: string;
  options: SelectOptionType[];
  value?: string;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  direction?: DirectionEnum;
  isExpanded?: boolean;
  validate?: (value: string) => string;
  onChange: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  ariaLabel?: string;
};

const SelectField = (props: SelectFieldPropsType) => {
  const [isExpanded, _setIsExpanded] = useState<boolean>(props.isExpanded || false);

  const [selectedOptionName, setSelectedOptionName] = useState<string>(() => {
    if (props.value) {
      const selectedOption = props.options.find((o) => o.value === props.value);
      return selectedOption ? selectedOption.name : '';
    }
    return '';
  });

  const isExpandedRef = useRef(isExpanded);
  const setIsExpanded = (data: boolean) => {
    isExpandedRef.current = data;
    _setIsExpanded(data);
  };

  const node = useRef<HTMLDivElement>(null);

  const handleOnClick = () => {
    setIsExpanded(false);
  };

  const toggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleOnToggle = (e: MouseEvent) => {
    if (node.current) {
      if (isExpandedRef.current && !node.current.contains(e.target as Node)) {
        toggle();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      toggle();
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleOnToggle);

    return () => {
      document.removeEventListener('mouseup', handleOnToggle);
    };
  }, []);

  useEffect(() => {
    if (props.value) {
      const v = props.options.find((option) => option.value === props.value);
      if (v) {
        setSelectedOptionName(v.name);
      }
    }
  }, [props.value]);

  return (
    <div
      id={props.id || props.name}
      ref={node}
      className={classNames(
        styles.wrapper,
        props.className,
        props.direction === DirectionEnum.Top ? styles.directionTop : styles.directionBottom
      )}
      aria-label={props.ariaLabel}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <SelectFieldContext.Provider
        value={{
          value: props.value,
          isExpanded,
          onClick: handleOnClick,
          onToggle: toggle,
        }}
      >
        <SelectFieldSelected
          className={classNames(props.selectedFieldClassname, props.disabled && styles.disabled)}
        >
          {selectedOptionName}
        </SelectFieldSelected>

        <SelectFieldList className={classNames(props.fieldListClassname)}>
          {props.options &&
            props.options.map((option) => (
              <SelectFieldOption
                key={option.value}
                id={option.value}
                value={option.value}
                onClick={(value) => {
                  const handleChangeEvent = {
                    target: {
                      id: props.name,
                      name: props.name,
                      value: value,
                    },
                  } as React.ChangeEvent<HTMLInputElement>;

                  props.onChange(value, handleChangeEvent);
                }}
                ariaLabel={`${props.ariaLabel}-${option.name}`}
              >
                <>{option.name}</>
              </SelectFieldOption>
            ))}
        </SelectFieldList>
      </SelectFieldContext.Provider>

      {props.touched && props.error && (
        <div className={styles.errorField}>{props.error && <div>{props.error}</div>}</div>
      )}
    </div>
  );
};

SelectField.Option = SelectFieldOption;
SelectField.List = SelectFieldList;
SelectField.Selected = SelectFieldSelected;
SelectField.DirectionEnum = DirectionEnum;

export default SelectField;
